import React, { useEffect, useState } from "react"

import { Form } from "antd"

import { useNavigate } from "react-router"
import FormSectionWrapper from "pages/dashboard/people/people/FormSectionWrapper"
import { AntDesignInput } from "components/antd/AntDesignInput"
import { IEwaBody, IEwaSettings } from "../EwaSettings"
import { useMutation, useQuery } from "@tanstack/react-query"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import PrevNextButton from "components/new-stuff/form/PrevNextButton"
import { IAllAdminsRes } from "stores/setting/permissions"
import { CustomSelect } from "components/new-stuff/form"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { onQueryError } from "utils/utils"

const CompanySettings = ({
  active,
  setActiveKey,
  setEwaBody,
  ewaBody,
}: {
  active: boolean
  setActiveKey: React.Dispatch<React.SetStateAction<string[]>>
  setEwaBody: Function
  ewaBody: IEwaBody
}) => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [cutOffDays, setCuttOffDays] = useState<string[]>([])
  const approvers = Form.useWatch("approvers", form)
  const onFinish = (values: any) => {
    const selectedApprovers =
      admins?.users
        ?.filter((admin) => approvers?.includes(admin.username))
        ?.map((admin) => ({
          first_name: admin.first_name,
          last_name: admin.last_name,
          email: admin.username,
        })) || []
    mixPanelEvent("settings-hr-payroll-ewa-update-init")

    submit({
      ...ewaBody,
      tin: values?.tinnumber,
      registered_name: values?.companyname,
      email: values?.email,
      phone: values?.phonenumber,
      full_address: values?.address,
      registration_number: values?.rcnumber,
      can_employees_withdraw: "yes",
      withdrawal_stop_day: values?.cutOff,
      withdrawal_fee_payer_id: "1",
      withdrawal_fee_payer: "EMPLOYEE",
      is_approval_required: String(
        ewaSettings?.is_approval_required ? "yes" : "no"
      ),
      approvers: selectedApprovers,
    })
  }
  const { isLoading: isGetEwaLoading, data: EwaDetailsRes } = useQuery<
    IBaseRes<any>
  >({
    queryKey: [COMPANY_ENDPOINTS.VIEW_SETTINGS, "ewa", ""],
    queryFn: async () => companyService.viewSettings({ id: "ewa", x: "" }),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const EwaDetails = EwaDetailsRes?.data
  const { isLoading: isAdminsLoading, data: adminsRes } =
    useQuery<IAllAdminsRes>({
      queryKey: [COMPANY_ENDPOINTS.VIEW_SETTINGS, { id: "admins", x: "" }],
      queryFn: async () => companyService.viewSettings({ id: "admins", x: "" }),
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
    })

  const admins = adminsRes?.data

  const { isLoading: isLoading, mutate: submit } = useMutation({
    mutationFn: async (body: IEwaBody) => companyService.updateEwa(body),
    onSuccess: (data: IBaseRes<any>) => {
      if (data.success) {
        setActiveKey(["3"])
        mixPanelEvent("settings-hr-payroll-ewa-update-success")
      }
    },
    onError: onQueryError,
  })

  const ewaSettings: IEwaSettings = EwaDetails?.ewaConfigSetting
  const getItemsBeforeSelectedOption = (
    array: string[],
    selectedOption: string
  ): string[] => {
    const selectedIndex = array.indexOf(selectedOption)
    if (selectedIndex !== -1) {
      return array.slice(0, selectedIndex)
    } else {
      return []
    }
  }
  useEffect(() => {
    if (ewaSettings?.payroll_run_day) {
      const offDaysOption = [
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
        "19",
        "20",
        "21",
        "22",
        "23",
        "24",
        "25",
        "26",
        "27",
        "28",
        "Last day",
      ]
      setCuttOffDays(
        getItemsBeforeSelectedOption(
          offDaysOption,
          String(ewaSettings?.payroll_run_day)
        )
      )
    }
  }, [ewaSettings])

  return (
    <FormSectionWrapper
      name="Company Information"
      title="Company Information"
      subtitle="In order to facilitate earned wage access services, we will collect and securely store company information, ensuring smooth processing of wage advances for employees"
      isActive={active}
      initialValues={{
        companyname: ewaSettings?.registered_name,
        address: ewaSettings?.full_address,
        email: ewaSettings?.email,
        phonenumber: ewaSettings?.phone,
        rcnumber: ewaSettings?.registration_number,
        tinnumber: ewaSettings?.tin || "",
        cutOff: ewaSettings?.withdrawal_stop_day,
        approvers: ewaSettings?.approvers
          ?.map((admin) => admin?.email)
          ?.filter((email) => email !== null),
      }}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={
        <PrevNextButton
          showPrevButton={false}
          submitting={isLoading}

          // onClickPrev={() => setActiveKey(["1"])}
        />
      }
    >
      <div className="grid md:grid-cols-2 gap-x-10 px-4 md:px-6 xl:px-[60px]">
        <div className="col-span-2">
          <AntDesignInput
            label="Company name"
            name="companyname"
            required={true}
          />
        </div>
        <AntDesignInput
          label="Address"
          name="address"
          required={true}
        />
        <AntDesignInput
          label="Email"
          name="email"
          required={true}
          type="email"
        />
        <AntDesignInput
          label="Company phone number"
          name="phonenumber"
          required={true}
        />
        <AntDesignInput
          label="RC number"
          name="rcnumber"
          required={true}
        />
        <AntDesignInput
          label="TIN number"
          name="tinnumber"
          required={true}
          maxLength={20}
        />
        <CustomSelect
          label="Cut off day"
          name="cutOff"
          tooltip="Withdrawal cut-off day must be before payroll run day"
          required
          options={cutOffDays?.map((option) => ({
            label: option,
            value: option,
          }))}
          placeholder="Please select"
          addBottomSpace
        />
        {ewaSettings?.is_approval_required && (
          <div className="col-span-2">
            <CustomSelect
              mode="multiple"
              showSearch
              label="Approvers"
              name="approvers"
              required
              isLoading={isAdminsLoading}
              options={admins?.users?.map((admin) => ({
                value: admin?.username,
                label: admin.full_name,
              }))}
              placeholder="Please Select"
              addBottomSpace
            />
          </div>
        )}
      </div>
    </FormSectionWrapper>
  )
}

export default CompanySettings
